// Samostatné okno zprávy: hlavička, přílohy, tělo; ribbon předchozí / následující, odpovědět / všem / předat, smazat, přesunout,
// přečtené, hvězdička, zdroj, hlavičky, stáhnout. Procházení jde v pořadí seznamu, ze kterého se okno otevřelo (stejná složka,
// filtr, hledání; další stránka seznamu se dotáhne). Akce volá na hlavní okno pošty (stejné kontroly práv), seznam v hlavním okně
// se obnoví přes události.
import { registerComponent } from '@core/views/factory';
import { action, ApiError } from '@core/api';
import { h, clear } from '@core/dom';
import { t, tk } from '@core/i18n';
import { IwRibbon, type RibbonButton } from '@core/components/ribbon';
import { IwCommandBar, type BarItem } from '@core/components/commandbar';
import { useBar, renderBarHeader } from '@core/components/chrome';
import { type MenuItem } from '@core/components/menu';
import { confirmDelete } from '@core/components/dialog';
import { toast } from '@core/components/toast';
import { events } from '@core/events';
import type { Account, Message, Row, Label } from './types';
import { renderMessage, folderName, openSource, openHeaders, openAttachment, printMessage, userLabels } from './message';
import { senderListable, senderMenu } from './listina';

interface Payload { message: Message; prefs: Record<string, unknown>; accounts: Account[]; labels?: Label[] }
/** Kontext seznamu z hlavního okna: pořadí zpráv a parametry dotazu pro dotažení dalších stránek. */
interface Nav { ids: string[]; folder: string; q: string; filter: string; sort?: string; dir?: string; first?: string; pageFrom: number; pageTo: number; total: number }
const PAGE = 50;

registerComponent('posta-message', ({ desc, win, wm }) => {
  const p = desc.payload as unknown as Payload;
  let m = p.message;
  const prefs = p.prefs;
  const accounts = p.accounts;
  const labels = p.labels ?? userLabels(prefs); // štítky jako v seznamu (Štítky ▾ na liště, čipy v hlavičce)
  const prefix = (JSON.parse(document.getElementById('iwan-boot')?.textContent ?? '{}') as { prefix?: string }).prefix ?? '';
  const ribbon = new IwRibbon();
  ribbon.setMode(wm.currentMode());
  // koncepce A: záhlaví Odpovědět · Odpovědět všem · Přeposlat · Smazat · Přesunout · ↑ ↓ · ⋯; telefon ← · předmět · ⋮
  const bar = useBar() ? new IwCommandBar() : null;
  const view = h('div', { class: 'mail-preview mail-single', tabindex: '-1' });
  const el = bar ? h('div', { class: 'view mail-view chrome-bar' }, view) : h('div', { class: 'view mail-view' }, ribbon, view);
  const remoteShown = (): boolean => !!el.querySelector('iframe[src*="images=1"]'); // tisk ukáže obrázky z internetu jen když jsou zobrazené
  const fileMode = (): boolean => m.folderRole === 'file'; // soubor .eml z úložiště: bez účtu, složky a příznaků
  let gone = false;
  let nav: Nav | null = null;
  let loading = false;
  const posta = <T,>(act: string, payload: Record<string, unknown>): Promise<T> => action<T>('posta', 'posta', act, {}, payload);

  const index = (): number => (nav ? nav.ids.indexOf(m.id) : -1);
  const position = (): string | undefined => { if (!nav || index() < 0) return undefined; return t('posta.ui.position', { i: (nav.pageFrom - 1) * PAGE + index() + 1, n: nav.total }); };
  const hasPrev = (): boolean => nav !== null && (index() > 0 || nav.pageFrom > 1);
  const hasNext = (): boolean => nav !== null && ((index() >= 0 && index() < nav.ids.length - 1) || nav.pageTo * PAGE < nav.total);

  function render(): void {
    clear(view);
    win.setTitle(m.subject || t('posta.ui.no_subject'), 'mail');
    const parts = renderMessage(m, {
      prefs, prefix, position: position(), labels,
      onNavigate: (dir) => void go(dir),
      wm,
      onItip: async (response, calendarId) => { try { await posta('itip', { id: m.id, response, calendar: calendarId }); toast(t('posta.itip.done_' + response), 'ok'); await show(m.id); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); } },
      openViewer: (fileId) => openAttachment(wm, m, fileId),
      sendReceipt: async (id) => { try { const r = await posta<{ sent: boolean }>('receipt', { id }); if (r.sent) toast(t('posta.receipt.sent'), 'ok'); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); } },
    });
    view.append(parts.head, parts.body);
    refreshBar();
  }
  function refreshBar(): void {
    if (!bar) {
      ribbon.setButtons(buttons());
      return;
    }
    const all = buttons();
    const place = (id: string): BarItem['place'] => (id === 'reply' ? 'primary' : ['reply_all', 'forward', 'delete', 'move', 'spam', 'labels', 'prev', 'next'].includes(id) ? 'action' : 'more');
    const order = ['reply', 'reply_all', 'forward', 'move', 'spam', 'labels', 'delete', 'prev', 'next']; // jako vzor: … · Smazat · ⋯ (šipky na konci)
    const items: BarItem[] = all.map((b) => ({ id: b.id, label: b.label, icon: b.icon, shortcut: b.shortcut, disabled: b.disabled, danger: b.danger, menu: b.menu, onClick: b.onClick, place: fileMode() ? 'action' : place(b.id), iconOnly: b.id === 'prev' || b.id === 'next' }))
      .filter((b) => !(b.id === 'prev' || b.id === 'next') || nav !== null)
      .filter((b) => !b.disabled) // co nejde (zpráva jen ke čtení, první / poslední zpráva), se schová, ne zašedne
      .sort((a, b) => (order.indexOf(a.id) < 0 ? 99 : order.indexOf(a.id)) - (order.indexOf(b.id) < 0 ? 99 : order.indexOf(b.id)));
    bar.setItems(items);
    renderBarHeader({ win, wm, bar, back: true });
  }

  /** Přechod na jinou zprávu: načíst, označit přečtenou podle nastavení, překreslit (okno zůstává). */
  async function show(id: string): Promise<void> {
    if (loading) return;
    loading = true;
    try {
      const markRead = String(prefs['markRead'] ?? 'now');
      m = await posta<Message>('message', { id, markSeen: markRead === 'now' });
      if (!m.seen && markRead === 'delay') window.setTimeout(() => { if (m.id === id) void setFlags({ seen: true }); }, 2000);
      render();
      view.scrollTop = 0;
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); } finally { loading = false; }
  }
  async function fetchPage(page: number): Promise<string[]> {
    if (!nav) return [];
    const r = await posta<{ rows: Row[]; total: number }>('list', { folder: nav.folder, q: nav.q, filter: nav.filter, page, sort: nav.sort ?? 'date', dir: nav.dir ?? 'desc', first: nav.first ?? '' });
    nav.total = r.total;
    return r.rows.map((x) => x.id);
  }
  async function go(delta: 1 | -1): Promise<void> {
    if (!nav) return;
    let i = index() + delta;
    if (i >= nav.ids.length && nav.pageTo * PAGE < nav.total) { nav.pageTo++; nav.ids.push(...await fetchPage(nav.pageTo)); }
    if (i < 0 && nav.pageFrom > 1) { nav.pageFrom--; const prev = await fetchPage(nav.pageFrom); nav.ids.unshift(...prev); i += prev.length; }
    const id = nav.ids[i];
    if (id) await show(id);
    else refreshBar();
  }

  async function setFlags(flags: { seen?: boolean; flagged?: boolean }): Promise<void> {
    try {
      await posta('flags', { ids: [m.id], ...flags });
      if (flags.seen !== undefined) m.seen = flags.seen;
      if (flags.flagged !== undefined) m.flagged = flags.flagged;
      render();
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  /** Po přesunu nebo smazání: přejít na další zprávu, když je; jinak zavřít. */
  async function afterRemoval(): Promise<void> {
    if (nav) {
      const i = index();
      if (i >= 0) nav.ids.splice(i, 1);
      nav.total = Math.max(0, nav.total - 1);
      const id = nav.ids[i] ?? nav.ids[i - 1];
      if (id) { await show(id); return; }
    }
    gone = true;
    win.close();
  }
  async function moveTo(folderId: string): Promise<void> {
    try { await posta('move', { ids: [m.id], folder: folderId }); toast(t('posta.ui.moved', { n: 1 }), 'ok'); await afterRemoval(); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  async function remove(): Promise<void> {
    if (m.folderRole === 'trash' && !(await confirmDelete())) return;
    try { await posta('delete', { ids: [m.id] }); toast(t('posta.ui.deleted', { n: 1 }), 'ok'); await afterRemoval(); } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  const compose = (mode: 'reply' | 'reply_all' | 'forward' | 'forward_attach') => void wm.open({ app: 'posta', window: 'napsat', params: { mode, message: m.id } });
  /** Štítek zapnout / vypnout (stejná akce jako v seznamu; seznam v hlavním okně se obnoví přes události). */
  async function toggleLabel(keyword: string): Promise<void> {
    const has = m.keywords.includes(keyword);
    try {
      await posta('keywords', { ids: [m.id], add: has ? [] : [keyword], remove: has ? [keyword] : [] });
      m.keywords = has ? m.keywords.filter((k) => k !== keyword) : [...m.keywords, keyword];
      render();
    } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
  }
  const labelMenu = (): MenuItem[] => labels.map((l) => ({ label: l.name, color: l.color, checked: m.keywords.includes(l.keyword), onSelect: () => void toggleLabel(l.keyword) }));
  const moveMenu = (): MenuItem[] => (accounts.find((a) => a.id === m.account)?.folders ?? []).filter((f) => f.id !== m.folder).map((f) => ({ label: folderName(f), icon: f.role === 'custom' ? 'folder' : undefined, onSelect: () => void moveTo(f.id) }));
  /** Je to spam / Není spam (balík G i v okně zprávy): přesun do Nevyžádané nebo z ní do Doručené – server naučí antispam. */
  const spamTarget = (): { spam: boolean; folder: string } | null => {
    const fs = accounts.find((a) => a.id === m.account)?.folders ?? [];
    const inSpam = m.folderRole === 'spam';
    const to = fs.find((f) => f.role === (inSpam ? 'inbox' : 'spam'));
    return to && !m.readOnly && m.folderRole !== 'sent' && m.folderRole !== 'drafts' ? { spam: !inSpam, folder: to.id } : null;
  };

  const buttons = (): RibbonButton[] => fileMode() ? [
    { id: 'source', label: t('posta.cmd.source'), icon: 'code', shortcut: 'Ctrl+U', group: 'core.grp.actions', onClick: () => openSource(wm, m) },
    { id: 'headers', label: t('posta.cmd.headers'), icon: 'list', group: 'core.grp.actions', onClick: () => void openHeaders(wm, m.id) },
    { id: 'print', label: t('posta.cmd.print'), icon: 'printer', shortcut: 'Ctrl+P', group: 'core.grp.actions', onClick: () => printMessage(m, prefix, remoteShown()) },
    { id: 'raw', label: t('posta.cmd.download_raw'), icon: 'download', group: 'core.grp.actions', disabled: !m.rawUrl, onClick: () => { if (m.rawUrl) window.open(m.rawUrl, '_blank'); } },
  ] : [
    { id: 'prev', label: t('posta.cmd.prev_msg'), icon: 'chevron-up', shortcut: '↑', group: 'posta.grp.nav', order: -2, disabled: !hasPrev(), onClick: () => void go(-1) },
    { id: 'next', label: t('posta.cmd.next_msg'), icon: 'chevron-down', shortcut: '↓', group: 'posta.grp.nav', order: -2, disabled: !hasNext(), onClick: () => void go(1) },
    { id: 'reply', label: t('posta.cmd.reply'), icon: 'reply', group: 'posta.grp.reply', order: -1, primary: true, onClick: () => compose('reply') },
    { id: 'reply_all', label: t('posta.cmd.reply_all'), icon: 'reply-all', group: 'posta.grp.reply', order: -1, onClick: () => compose('reply_all') },
    { id: 'forward', label: t('posta.cmd.forward'), icon: 'forward', group: 'posta.grp.reply', order: -1, onClick: () => compose('forward') },
    { id: 'forward_attach', label: t('posta.cmd.forward_attach'), icon: 'paperclip', group: 'posta.grp.reply', order: -1, onClick: () => compose('forward_attach') },
    { id: 'delete', label: t('posta.cmd.delete'), icon: 'trash', group: 'core.grp.manage', order: 1, danger: true, disabled: m.readOnly === true, onClick: () => void remove() },
    { id: 'move', label: t('posta.cmd.move'), icon: 'folder', group: 'core.grp.manage', order: 1, disabled: m.readOnly === true, menu: m.readOnly ? [] : moveMenu() },
    ...((): RibbonButton[] => {
      const sp = spamTarget();
      return [{ id: 'spam', label: t(sp?.spam === false ? 'posta.cmd.not_spam' : 'posta.cmd.is_spam'), icon: sp?.spam === false ? 'check' : 'alert', group: 'core.grp.manage', order: 1, disabled: !sp, onClick: () => { if (sp) void moveTo(sp.folder); } }];
    })(),
    { id: 'sender', label: t('posta.cmd.sender'), icon: 'shield', group: 'core.grp.manage', order: 1, disabled: !senderListable(accounts, m), menu: senderListable(accounts, m) ? senderMenu(m) : [] },
    { id: 'labels', label: t('posta.cmd.labels'), icon: 'tag', group: 'posta.grp.mark', order: 2, disabled: m.readOnly === true || !labels.length, menu: m.readOnly ? [] : labelMenu() },
    { id: 'seen', label: t(m.seen ? 'posta.cmd.unseen' : 'posta.cmd.seen'), icon: 'check', group: 'posta.grp.mark', order: 2, onClick: () => void setFlags({ seen: !m.seen }) },
    { id: 'flag', label: t(m.flagged ? 'posta.cmd.unflag' : 'posta.cmd.flag'), icon: 'star', group: 'posta.grp.mark', order: 2, disabled: m.readOnly === true, onClick: () => void setFlags({ flagged: !m.flagged }) },
    { id: 'source', label: t('posta.cmd.source'), icon: 'code', shortcut: 'Ctrl+U', group: 'core.grp.actions', order: 3, disabled: !m.rawId, onClick: () => openSource(wm, m) },
    { id: 'headers', label: t('posta.cmd.headers'), icon: 'list', group: 'core.grp.actions', order: 3, onClick: () => void openHeaders(wm, m.id) },
    { id: 'print', label: t('posta.cmd.print'), icon: 'printer', shortcut: 'Ctrl+P', group: 'core.grp.actions', order: 3, onClick: () => printMessage(m, prefix, remoteShown()) },
    { id: 'raw', label: t('posta.cmd.download_raw'), icon: 'download', group: 'core.grp.actions', order: 3, disabled: !m.rawUrl, onClick: () => { if (m.rawUrl) window.open(m.rawUrl, '_blank'); } },
  ];

  // zpráva změněná jinde (příznaky, přesun): znovu načíst; když zmizela, zavřít
  const unsub = events.subscribe(['posta.zpravy'], () => {
    if (gone || loading || fileMode()) return;
    const id = m.id;
    void posta<Message>('message', { id, markSeen: false }).then((mm) => { if (m.id === id) { m = mm; render(); } }).catch(() => { gone = true; win.close(); });
  }, {});

  render();
  return {
    el,
    setMode: (mode) => (bar ? refreshBar() : ribbon.setMode(mode)),
    getState: () => ({}),
    setState: (s) => { const st = s as { nav?: Nav } | null; if (st?.nav) { nav = { ...st.nav, ids: [...st.nav.ids] }; render(); } },
    focus: () => view.focus(),
    destroy: () => unsub(),
    shortcut: (sc: string): boolean => {
      // šipky a j/k jako v seznamu (zkratky okna přijdou, dokud není fokus uvnitř rámu se zprávou)
      if (sc === 'ArrowDown' || sc === 'J') { void go(1); return true; }
      if (sc === 'ArrowUp' || sc === 'K') { void go(-1); return true; }
      if (sc === 'Ctrl+R') { compose('reply'); return true; }
      if (sc === 'Ctrl+U') { openSource(wm, m); return true; }
      if (sc === 'Ctrl+P') { printMessage(m, prefix, remoteShown()); return true; }
      if (sc === 'Delete') { if (!m.readOnly) void remove(); return true; }
      if (sc === 'Esc' && bar) { void win.close(); return true; }
      return false;
    },
  } as ReturnType<Parameters<typeof registerComponent>[1]> & { shortcut(sc: string): boolean };
});

export {};
